import React, { useEffect, useMemo, useState } from "react";
import {
  getAuth,
  onAuthStateChanged,
  multiFactor,
  PhoneAuthProvider,
  RecaptchaVerifier,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
} from "firebase/auth";
import "./Signup.css"; // reuse SU- styles
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react"; // add this import at the top

/** ---------- Simple gate you can call anywhere ----------
 * Example use: await requireMfaOrRedirect({ requireAllUsers: true })
 */
export async function requireMfaOrRedirect({
  requireAllUsers = false, // if false, you can choose to require only mods/admins via custom claim outside this helper
  redirectTo = "/security",
} = {}) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return; // not signed in — handle upstream

  // Force refresh to pick up any custom claims (if you’re using them)
  await user.getIdToken(true);

  // If you want to enforce only for certain users, fetch claims here and branch.
  // const token = await user.getIdTokenResult();
  // if (!requireAllUsers && !token.claims.requireMfa) return;

  const enrolled = (multiFactor(user)?.enrolledFactors || []).length;
  if (enrolled === 0) {
    window.location.assign(redirectTo);
    throw new Error("MFA required; redirecting to enrollment.");
  }
}

function normalizePhone(input) {
  if (!input) return "";
  // Remove spaces, dashes, parentheses
  let digits = input.replace(/[^\d+]/g, "");
  // If it starts with "1" and has 11 digits, prefix with +
  if (/^1\d{10}$/.test(digits)) {
    return "+" + digits;
  }
  // If it already starts with +, trust it
  if (digits.startsWith("+")) {
    return digits;
  }
  // If it’s just 10 digits, assume US/Canada and add +1
  if (/^\d{10}$/.test(digits)) {
    return "+1" + digits;
  }
  return digits; // fallback
}

/** ---------- /security page component ----------
 * Allows user to enroll either TOTP or SMS.
 */
export default function Security() {
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);
  const [user, setUser] = useState(null);

  // --------- Common UI state ----------
  const [status, setStatus] = useState(""); // non-fatal info
  const [error, setError] = useState("");

  // --------- TOTP state ----------
  const [totpSecret, setTotpSecret] = useState(null); // secret object from Firebase
  const [totpQrUrl, setTotpQrUrl] = useState(""); // best-effort QR url (if provided)
  const [totpKey, setTotpKey] = useState(""); // Base32 secret key
  const [totpCode, setTotpCode] = useState(""); // 6-digit from authenticator
  const [totpEnrolling, setTotpEnrolling] = useState(false);

  // --------- SMS state ----------
  const [phone, setPhone] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [smsVerificationId, setSmsVerificationId] = useState(null);
  const [smsSending, setSmsSending] = useState(false);
  const [smsVerifying, setSmsVerifying] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, [auth]);

  // Helpers
  const withReset = (fn) => async (...args) => {
    setError(""); setStatus("");
    try { await fn(...args); } catch (e) {
      console.error(e);
      setError(e?.message || String(e));
    }
  };

  // ---------- TOTP: Generate secret & show QR ----------
  const startTotpEnrollment = withReset(async () => {
    if (!user) throw new Error("Not signed in.");
    setTotpEnrolling(true);
    try {
      const mfa = multiFactor(user);
      const session = await mfa.getSession();

      // Generate secret for this user/session
      const secret = await TotpMultiFactorGenerator.generateSecret(session);

      // The object shape may vary by SDK version; do best-effort extraction:
      // Common fields: secretKey (base32), qrCodeUrl/provisioningUri
      const secretKey = secret.secretKey || secret.base32 || "";
      // Build otpauth:// URI manually
      const accountName = encodeURIComponent(user.email || user.uid);
      const issuer = encodeURIComponent("iSpeakWell");
      const qrUrl = `otpauth://totp/${issuer}:${accountName}?secret=${secretKey}&issuer=${issuer}&algorithm=SHA1&digits=6&period=30`;
      setTotpSecret(secret);
      setTotpKey(secretKey);
      setTotpQrUrl(qrUrl);
      setStatus("Scan the QR in an authenticator app, then enter the 6-digit code.");
    } finally {
      setTotpEnrolling(false);
    }
  });

  // ---------- TOTP: Complete enrollment ----------
  const completeTotpEnrollment = withReset(async () => {
    if (!user) throw new Error("Not signed in.");
    if (!totpSecret) throw new Error("Start TOTP enrollment first.");
    if (!totpCode) throw new Error("Enter the 6-digit code from your app.");

    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
      totpSecret,
      totpCode
    );

    await multiFactor(user).enroll(assertion, "authenticator");
    setStatus("✅ TOTP enrolled! You can add SMS too (optional).");
    // Clear inputs
    setTotpCode("");
  });

  // ---------- SMS: Request code ----------
  const sendSmsCode = withReset(async () => {
    if (!user) throw new Error("Not signed in.");
    if (!phone) throw new Error("Enter a phone number (E.164, e.g., +1 555-555-5555).");
    if (!/^\+\d{10,15}$/.test(phone)) {
      throw new Error("Invalid phone format. Use e.g., +15555555555");
    }
    setSmsSending(true);
    try {
      // Create a separate invisible reCAPTCHA for enrollment
      if (!window.recaptchaVerifierEnroll) {
        window.recaptchaVerifierEnroll = new RecaptchaVerifier(
          auth,
          "recaptcha-enroll-container",
          { size: "invisible" }
        );
      }
      const provider = new PhoneAuthProvider(auth);
      const verificationId = await provider.verifyPhoneNumber(
        phone,
        window.recaptchaVerifierEnroll
      );
      setSmsVerificationId(verificationId);
      setStatus("SMS sent. Enter the 6-digit code to finish enrollment.");
    } finally {
      setSmsSending(false);
    }
  });

  // ---------- SMS: Complete enrollment ----------
  const completeSmsEnrollment = withReset(async () => {
    if (!user) throw new Error("Not signed in.");
    if (!smsVerificationId) throw new Error("Request an SMS code first.");
    if (!smsCode) throw new Error("Enter the 6-digit SMS code.");

    setSmsVerifying(true);
    try {
      const cred = PhoneAuthProvider.credential(smsVerificationId, smsCode);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      await multiFactor(user).enroll(assertion, "phone number");
      setStatus("✅ SMS enrolled! You’re protected with MFA.");
      setSmsCode("");
    } finally {
      setSmsVerifying(false);
    }
  });

  const enrolledFactors = (user && multiFactor(user)?.enrolledFactors) || [];
  console.log("__________________: ",totpSecret?.provisioningUri);
  return (
    <div className="SU-signup-container" style={{ paddingTop: 24 }}>
      <h1 className="SU-supheader-title">Account Security</h1>
      <p className="SU-subtitle">Add a second factor to protect your account and our community.</p>

      <div className="SU-card">
        <h3 className="SU-card-title">Your MFA Status</h3>
        <div className="SU-SignUp-Input row-span-2" style={{ marginBottom: 8 }}>
          <div className="row-span-2" style={{ gridColumn: "1 / -1" }}>
            {enrolledFactors.length > 0 ? (
              <p style={{ color: "var(--isu-success)" }}>
                ✅ You have {enrolledFactors.length} enrolled factor(s).
              </p>
            ) : (
              <p style={{ color: "var(--isu-text-dim)" }}>
                You haven’t enrolled any second factors yet.
              </p>
            )}
          </div>
        </div>

        {/* --- TOTP (authenticator app) --- */}
        <h3 className="SU-card-title">1) Authenticator App (Recommended & Free)</h3>
        <div className="SU-SignUp-Input">
          {!totpSecret ? (
            <button
              className="SUButtons"
              type="button"
              disabled={totpEnrolling}
              onClick={startTotpEnrollment}
            >
              {totpEnrolling ? "Preparing…" : "Set up Authenticator"}
            </button>
          ) : (
            <>
              <div className="row-span-2">
                <div style={{ display: "grid", justifyItems: "center", gap: 8 }}>
                   {totpQrUrl && (
                      <QRCodeSVG
                        value={totpQrUrl}
                        size={200}
                        includeMargin={true}
                        bgColor="#ffffff"
                        fgColor="#000000"
                      />
                    )}
                  <small className="SU-tagline">
                    If QR doesn’t scan, enter this key manually:{" "}
                    <code style={{ userSelect: "all" }}>{totpKey || "(secret key)"}</code>
                  </small>
                </div>
              </div>

              <input
                className="SU-input-sms"
                type="text"
                placeholder="Enter 6-digit code from app"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
              />
              <button className="SUButtons" type="button" onClick={completeTotpEnrollment}>
                Confirm & Enroll
              </button>
            </>
          )}
        </div>

        {/* --- SMS (phone) --- */}
        <h3 className="SU-card-title" style={{ marginTop: 22 }}>2) Text Message (SMS)</h3>
        <div className="SU-SignUp-Input">
          <input
            type="tel"
            placeholder="+15555555555"
            value={phone}
            onChange={(e) => setPhone(normalizePhone(e.target.value))}
          />
          <button
            className="SUButtons"
            type="button"
            disabled={smsSending}
            onClick={sendSmsCode}
          >
            {smsSending ? "Sending…" : "Send Code"}
          </button>

          {smsVerificationId && (
            <div className="SU-mfa-section">
              <h4>Enter SMS code:</h4>
              <input
                className="SU-input-sms"
                type="text"
                placeholder="123456"
                value={smsCode}
                onChange={(e) => setSmsCode(e.target.value)}
              />
              <button
                className="SUButtons"
                type="button"
                disabled={smsVerifying}
                onClick={completeSmsEnrollment}
              >
                {smsVerifying ? "Verifying…" : "Verify & Enroll"}
              </button>
            </div>
          )}
        </div>

        {status && <p className="SU-subtitle" style={{ marginTop: 12 }}>{status}</p>}
        {error && <p className="SU-error-message">{error}</p>}

        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          <button className="SUButtons SUButtons--ghost" type="button" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </div>

      {/* Invisible reCAPTCHA container for SMS ENROLLMENT (separate from signup/login ones) */}
      <div id="recaptcha-enroll-container" style={{ display: "none" }} />
    </div>
  );
}
